import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

type Theme = "light" | "dark";

const STORAGE_KEY = "clubghor.admin.theme";
const ThemeContext = createContext<{ theme: Theme; toggle: () => void; setTheme: (t: Theme) => void } | null>(null);

function currentTheme(): Theme {
  if (typeof document === "undefined") return "light";
  return document.documentElement.dataset.theme === "dark" ? "dark" : "light";
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  // index.html already applied the stored or system theme before first paint, so
  // there is no flash and no need to re-derive it here.
  const [theme, setThemeState] = useState<Theme>(currentTheme);

  const apply = useCallback((next: Theme) => {
    const root = document.documentElement;

    /**
     * Suppress transitions for the swap.
     *
     * A theme flip changes colour, background, border and shadow on nearly every
     * element at once. Left alone, every transition on those properties fires
     * together and the whole page smears instead of snapping. Kill transitions,
     * force a reflow, then restore on the next frame.
     */
    const style = document.createElement("style");
    style.appendChild(document.createTextNode("*,*::before,*::after{transition:none !important}"));
    document.head.appendChild(style);

    root.dataset.theme = next;
    root.classList.toggle("dark", next === "dark");

    // Force a synchronous reflow so the style change is committed without
    // transitions, then allow them again.
    void root.offsetHeight;
    window.requestAnimationFrame(() => {
      style.remove();
    });

    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Private mode or blocked storage: the theme still applies for this session.
    }
    setThemeState(next);
  }, []);

  const toggle = useCallback(() => {
    apply(currentTheme() === "dark" ? "light" : "dark");
  }, [apply]);

  // Follow the OS only while the operator has not made an explicit choice.
  useEffect(() => {
    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = (event: MediaQueryListEvent) => {
      let stored: string | null = null;
      try {
        stored = localStorage.getItem(STORAGE_KEY);
      } catch {
        stored = null;
      }
      if (!stored) apply(event.matches ? "dark" : "light");
    };
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, [apply]);

  const value = useMemo(() => ({ theme, toggle, setTheme: apply }), [theme, toggle, apply]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) throw new Error("useTheme must be used inside a ThemeProvider");
  return context;
}
